import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { getProbeHistory } from "../lib/api";
import { ProbeRun } from "../types";
import { Bone } from "../components/Skeleton";

export default function History() {
  const [offset, setOffset] = useState(0);
  const limit = 20;

  const { data, isLoading, isError } = useQuery({
    queryKey: ["history", offset],
    queryFn: () => getProbeHistory(limit, offset),
  });

  const runs: ProbeRun[] = data?.runs ?? [];

  return (
    <div style={{ minHeight: "100vh" }}>
      <div style={{ maxWidth: 800, margin: "0 auto", padding: "40px 24px" }}>
        <Link to="/" style={backLink}>← Back</Link>
        <h1 style={{ margin: "0 0 24px", fontSize: 26, fontWeight: 700, color: "#EFF2F5" }}>Run History</h1>

        {isLoading ? (
          <div className="glass" style={{ overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                  <th style={th("left")}>URL</th>
                  <th style={th("right")}>Method</th>
                  <th style={th("right")}>Status</th>
                  <th style={th("right")}>P95</th>
                  <th style={th("right")}>Date</th>
                  <th style={th("right")}>Share</th>
                </tr>
              </thead>
              <tbody>
                {[240, 200, 260, 180, 220, 190, 250, 210].map((urlW, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                    <td style={{ ...td }}><Bone w={urlW} h={13} /></td>
                    <td style={{ ...td, textAlign: "right" }}><Bone w={36} h={13} style={{ marginLeft: "auto" }} /></td>
                    <td style={{ ...td, textAlign: "right" }}><Bone w={60} h={13} style={{ marginLeft: "auto" }} /></td>
                    <td style={{ ...td, textAlign: "right" }}><Bone w={44} h={13} style={{ marginLeft: "auto" }} /></td>
                    <td style={{ ...td, textAlign: "right" }}><Bone w={72} h={13} style={{ marginLeft: "auto" }} /></td>
                    <td style={{ ...td, textAlign: "right" }}><Bone w={30} h={13} style={{ marginLeft: "auto" }} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : isError ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#98A1AB" }}>
            <p style={{ fontSize: 15, color: "#EFF2F5" }}>Service temporarily unavailable</p>
            <p style={{ fontSize: 13 }}>We're having trouble connecting to our data service. Please try refreshing the page.</p>
          </div>
        ) : runs.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#98A1AB" }}>
            <p>No runs yet. <Link to="/" style={{ color: "#FF2566" }}>Run your first probe →</Link></p>
          </div>
        ) : (
          <>
            <div className="glass" style={{ overflow: "hidden" }}>
              <table className="tbl-hover" style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                    <th style={th("left")}>URL</th>
                    <th style={th("right")}>Method</th>
                    <th style={th("right")}>Status</th>
                    <th style={th("right")}>P95</th>
                    <th style={th("right")}>Date</th>
                    <th style={th("right")}>Share</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r) => (
                    <tr key={r.run_id} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                      <td style={{ ...td, maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#EFF2F5" }}>
                        {r.url}
                      </td>
                      <td style={{ ...td, textAlign: "right", color: "#98A1AB" }}>{r.method}</td>
                      <td style={{ ...td, textAlign: "right" }}>
                        <span style={{ color: r.status === "complete" ? "#39D98A" : r.status === "error" ? "#FF5C5C" : "#FDDD48" }}>
                          {r.status}
                        </span>
                      </td>
                      <td style={{ ...td, textAlign: "right", fontWeight: 600, color: "#EFF2F5" }}>
                        {r.percentiles?.p95 != null ? `${r.percentiles.p95}ms` : "—"}
                      </td>
                      <td style={{ ...td, textAlign: "right", color: "#98A1AB", whiteSpace: "nowrap" }}>
                        {new Date(r.created_at).toLocaleDateString()}
                      </td>
                      <td style={{ ...td, textAlign: "right" }}>
                        <a href={`/r/${r.run_id}`} style={{ color: "#98A1AB", fontSize: 12 }}>View</a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16 }}>
              <button onClick={() => setOffset((o) => Math.max(0, o - limit))} disabled={offset === 0} className="btn-glass" style={{ padding: "8px 16px", fontSize: 13 }}>
                ← Previous
              </button>
              <button onClick={() => setOffset((o) => o + limit)} disabled={runs.length < limit} className="btn-glass" style={{ padding: "8px 16px", fontSize: 13 }}>
                Next →
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function th(align: "left" | "right"): React.CSSProperties {
  return { padding: "10px 16px", textAlign: align, fontSize: 11, fontWeight: 700, color: "#98A1AB", textTransform: "uppercase", letterSpacing: "0.06em" };
}
const td: React.CSSProperties = { padding: "13px 16px" };
const backLink: React.CSSProperties = { display: "inline-block", marginBottom: 20, fontSize: 13, color: "#98A1AB", textDecoration: "none" };
